import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AlertConfiguration = () => {
  const [activeTab, setActiveTab] = useState('alerts');

  const alertRules = [
    {
      id: 1,
      name: 'Position Limit Breach',
      category: 'Risk Management',
      description: 'Alert when position exceeds 80% of regulatory limit',
      enabled: true,
      priority: 'critical',
      threshold: '80%',
      channels: ['email', 'sms', 'dashboard'],
      recipients: ['john.trader@tradepulse.com', 'risk@tradepulse.com'],
      lastTriggered: '2025-09-20T15:30:00Z',
      triggerCount: 3
    },
    {
      id: 2,
      name: 'Reporting Deadline Approaching',
      category: 'Compliance',
      description: 'Notify 24 hours before regulatory reporting deadline',
      enabled: true,
      priority: 'high',
      threshold: '24 hours',
      channels: ['email', 'dashboard'],
      recipients: ['compliance@tradepulse.com', 'sarah.chen@tradepulse.com'],
      lastTriggered: '2025-09-21T08:00:00Z',
      triggerCount: 12
    },
    {
      id: 3,
      name: 'Document Expiry Warning',
      category: 'Documentation',
      description: 'Alert when compliance documents expire within 30 days',
      enabled: true,
      priority: 'medium',
      threshold: '30 days',
      channels: ['email'],
      recipients: ['documents@tradepulse.com'],
      lastTriggered: '2025-09-19T10:15:00Z',
      triggerCount: 5
    },
    {
      id: 4,
      name: 'Unusual Trading Activity',
      category: 'Risk Management',
      description: 'Detect unusual trading patterns or volumes',
      enabled: false,
      priority: 'high',
      threshold: '200% of average',
      channels: ['email', 'sms'],
      recipients: ['risk@tradepulse.com', 'compliance@tradepulse.com'],
      lastTriggered: null,
      triggerCount: 0
    },
    {
      id: 5,
      name: 'Failed Transaction Reporting',
      category: 'Compliance',
      description: 'Alert when transaction reporting fails or is rejected',
      enabled: true,
      priority: 'critical',
      threshold: 'Immediate',
      channels: ['email', 'sms', 'dashboard'],
      recipients: ['compliance@tradepulse.com', 'it@tradepulse.com'],
      lastTriggered: '2025-09-18T14:45:00Z',
      triggerCount: 1
    }
  ];

  const notificationChannels = [
    {
      id: 1,
      type: 'email',
      name: 'Email Notifications',
      description: 'Send alerts via email to specified recipients',
      enabled: true,
      config: {
        server: 'smtp.tradepulse.com',
        port: '587',
        encryption: 'TLS'
      }
    },
    {
      id: 2,
      type: 'sms',
      name: 'SMS Notifications',
      description: 'Send critical alerts via SMS for immediate attention',
      enabled: true,
      config: {
        provider: 'Twilio',
        region: 'US-East'
      }
    },
    {
      id: 3,
      type: 'dashboard',
      name: 'Dashboard Alerts',
      description: 'Display alerts in the application dashboard',
      enabled: true,
      config: {
        retention: '30 days',
        maxAlerts: '100'
      }
    },
    {
      id: 4,
      type: 'webhook',
      name: 'Webhook Integration',
      description: 'Send alerts to external systems via HTTP webhooks',
      enabled: false,
      config: {
        endpoint: 'https://api.external-system.com/alerts',
        timeout: '30 seconds'
      }
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error bg-error/10 border-error/20';
      case 'high': return 'text-warning bg-warning/10 border-warning/20';
      case 'medium': return 'text-accent bg-accent/10 border-accent/20';
      case 'low': return 'text-muted-foreground bg-muted border-border';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Risk Management': return 'Shield';
      case 'Compliance': return 'CheckCircle';
      case 'Documentation': return 'FileText';
      default: return 'Bell';
    }
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'email': return 'Mail';
      case 'sms': return 'MessageSquare';
      case 'dashboard': return 'Monitor';
      case 'webhook': return 'Webhook';
      default: return 'Bell';
    }
  };

  const formatLastTriggered = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Less than 1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date?.toLocaleDateString();
  };

  const tabs = [
    { id: 'alerts', label: 'Alert Rules', icon: 'Bell' },
    { id: 'channels', label: 'Notification Channels', icon: 'Send' },
    { id: 'recipients', label: 'Recipients', icon: 'Users' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Settings" size={18} className="text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Alert Configuration</h2>
              <p className="text-sm text-muted-foreground">Configure compliance alerts and notification preferences</p>
            </div>
          </div>
          <Button variant="outline" iconName="Plus" iconPosition="left" size="sm">
            New Alert Rule
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-200
                ${activeTab === tab?.id
                  ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {activeTab === 'alerts' && (
          <div className="space-y-4">
            {alertRules?.map(rule => (
              <div key={rule?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={getCategoryIcon(rule?.category)} size={18} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-foreground">{rule?.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(rule?.priority)}`}>
                          {rule?.priority?.toUpperCase()}
                        </span>
                        <div className="flex items-center gap-1">
                          <Checkbox
                            checked={rule?.enabled}
                            onChange={() => {}}
                            size="sm"
                          />
                          <span className="text-xs text-muted-foreground">Enabled</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{rule?.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-muted-foreground">
                        <div>
                          <div className="flex items-center gap-1 mb-1">
                            <Icon name="Target" size={12} />
                            <span className="font-medium">Threshold:</span>
                            <span>{rule?.threshold}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-1">
                            <Icon name="Activity" size={12} />
                            <span className="font-medium">Last triggered:</span>
                            <span>{formatLastTriggered(rule?.lastTriggered)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="BarChart" size={12} />
                            <span className="font-medium">Trigger count:</span>
                            <span>{rule?.triggerCount}</span>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 mb-1">
                            <Icon name="Send" size={12} />
                            <span className="font-medium">Channels:</span>
                            <span>{rule?.channels?.join(', ')}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="Users" size={12} />
                            <span className="font-medium">Recipients:</span>
                            <span>{rule?.recipients?.length} configured</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button variant="ghost" size="sm" iconName="Edit" />
                    <Button variant="ghost" size="sm" iconName="Copy" />
                    <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'channels' && (
          <div className="space-y-4">
            {notificationChannels?.map(channel => (
              <div key={channel?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={getChannelIcon(channel?.type)} size={18} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-foreground">{channel?.name}</h3>
                        <div className="flex items-center gap-1">
                          <Checkbox
                            checked={channel?.enabled}
                            onChange={() => {}}
                            size="sm"
                          />
                          <span className="text-xs text-muted-foreground">Enabled</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{channel?.description}</p>
                      
                      <div className="bg-muted/50 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-foreground mb-2">Configuration</h4>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          {Object.entries(channel?.config)?.map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="capitalize">{key?.replace(/([A-Z])/g, ' $1')}:</span>
                              <span className="font-medium">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button variant="ghost" size="sm" iconName="Settings" />
                    <Button variant="ghost" size="sm" iconName="TestTube" />
                    <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'recipients' && (
          <div className="space-y-6">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-4">Recipient Groups</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-foreground">Compliance Team</h4>
                    <p className="text-sm text-muted-foreground">Primary compliance officers and managers</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">3 members</span>
                    <Button variant="ghost" size="sm" iconName="Edit" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-foreground">Risk Management</h4>
                    <p className="text-sm text-muted-foreground">Risk managers and analysts</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">4 members</span>
                    <Button variant="ghost" size="sm" iconName="Edit" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-foreground">Trading Desk</h4>
                    <p className="text-sm text-muted-foreground">Active traders and desk heads</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">6 members</span>
                    <Button variant="ghost" size="sm" iconName="Edit" />
                  </div>
                </div>
              </div>

              <Button variant="outline" iconName="Plus" iconPosition="left" size="sm" className="mt-4">
                Add Recipient Group
              </Button>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-4">Individual Recipients</h3>
              
              <div className="space-y-2">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="Enter email address"
                  className="mb-2"
                />
                <Input
                  label="Phone Number (SMS)"
                  type="tel"
                  placeholder="Enter phone number"
                  className="mb-2"
                />
                <Button variant="outline" iconName="Plus" iconPosition="left" size="sm">
                  Add Recipient
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertConfiguration;